import * as React from 'react';
import { isEmpty } from 'lodash';
import { addDays, format, getTime, parse } from 'date-fns';

import {
  IControlError,
  IControlSchemaBase,
  ValidationResult,
} from '../../../core/Controls';
import { Component, I18NString } from '../../helpers/Utils';
import { IDispatcher } from '../../helpers/Dispatcher';

import DateTime from '../components/Datetime';
import Field from '../components/Field';

import ErrorAlert from '../components/ErrorAlert';

export interface IRangeCalendarValue {
  beginDate: string;
  finishDate: string;
}

export interface IRangeCalendarSchema extends IControlSchemaBase {
  beginDate: I18NString;
  finishDate: I18NString;
  description: I18NString;
  notification: I18NString;
  value?: IRangeCalendarValue[] | IRangeCalendarValue | any;
  disabled?: boolean;
  currentDate: string;
  isMore?: boolean;
  isLess?: boolean;
  extra?: any;
}

interface ICalendarProps {
  schema: IRangeCalendarSchema;
  api: any;
  dispatcher?: IDispatcher;
  error: IControlError | undefined;
  visited: boolean;
}

interface State {
  isOpen: boolean;
  isOpenNotification: boolean;
  beginVisited: boolean;
  endVisited: boolean;
  value?: {
    beginDate: string;
    finishDate: string;
  }[];
  multiple: boolean;
}

class RangeCalendar extends Component<ICalendarProps, State> {
  state = {
    isOpen: false,
    isOpenNotification: true,
    beginVisited: false,
    endVisited: false,
    value: [
      {
        beginDate: '',
        finishDate: '',
      },
    ],
    multiple: false,
  };

  // eslint-disable-next-line react/sort-comp
  componentDidUpdate(prevProps: Readonly<ICalendarProps>) {
    if (prevProps !== this.props) {
      this.setState({
        // Если пришел объект с диапазоном, добавляем его в массив, обработал также случай когда приходит undefined
        value:
          typeof this.props.schema.value === 'object' &&
          this.props.schema.value?.length
            ? this.props.schema.value
            : [
                this.props.schema?.value ?? {
                  beginDate: '',
                  finishDate: '',
                },
              ],
        multiple:
          this.props?.schema.type.toUpperCase() === 'RANGEDMULTICALENDAR',
      });
    }
  }

  onClick = () => this.setState(({ isOpen }) => ({ isOpen: !isOpen }));

  onOpenNotification = () =>
    this.setState(({ isOpenNotification }) => ({
      isOpenNotification: !isOpenNotification,
    }));

  onChange(date: string, name: string, index: number) {
    const { dispatcher } = this.props;
    const { value } = this.state;

    switch (name) {
      case 'begin': {
        const result = [...value];
        result[index].beginDate = date;

        this.setState(
          {
            beginVisited: true,
            value: result,
          },
          () => this.setVisitedIfNeeded(),
        );
        break;
      }
      case 'finish': {
        const result = [...value];
        result[index].finishDate = date;

        this.setState(
          {
            endVisited: true,
            value: result,
          },
          () => this.setVisitedIfNeeded(),
        );
        break;
      }
      default:
        break;
    }

    const validationResult = this.validationResult(value);
    dispatcher.onBlur(
      this.props.schema.id,
      value.length > 1 ? value : value[0],
      !validationResult,
      validationResult,
    );
  }

  setVisitedIfNeeded() {
    if (this.state.beginVisited && this.state.endVisited) {
      this.props.dispatcher.onVisit(this.props.schema.id);
    }
  }

  onBlur = (name: string) => {
    switch (name) {
      case 'begin': {
        this.setState({ beginVisited: true }, () => this.setVisitedIfNeeded());
        break;
      }
      case 'finish': {
        this.setState({ endVisited: true }, () => this.setVisitedIfNeeded());
        break;
      }
      default:
        break;
    }
  };

  handlerMultipleAction = (action: 'add' | 'remove') => () => {
    const result = [...this.state.value];
    const {
      schema: { extra },
    } = this.props;

    let minDateStringForMultiple;
    if (this.state.multiple && result[result.length - 1]) {
      if (result[result.length - 1]?.finishDate) {
        const duration =
          extra?.multiple?.duration === 0 ? 1 : extra?.multiple?.duration;
        const minDate = addDays(
          parse(
            result[result.length - 1]?.finishDate,
            'dd.MM.yyyy',
            new Date(),
          ),
          duration,
        );
        minDateStringForMultiple = format(minDate, 'dd.MM.yyyy');
      }
    }

    if (action === 'add') {
      result.push({
        beginDate:
          typeof extra?.multiple?.duration === 'number' && result.length > 0
            ? minDateStringForMultiple
            : '',
        finishDate: '',
      });
    } else {
      result.pop();
    }

    this.setState({
      value: [...result],
    });

    const validationResult = this.validationResult(result);
    this.props.dispatcher.onBlur(
      this.props.schema.id,
      result.length > 1 ? result : result[0],
      !validationResult,
      validationResult,
    );
  };

  // eslint-disable-next-line class-methods-use-this
  validationResult(value: IRangeCalendarValue[] | any): ValidationResult {
    let result;

    for (const item of value) {
      const beginDateUnix = getTime(new Date(item?.beginDate));
      const finishDateUnix = getTime(new Date(item?.finishDate));

      if (
        (isEmpty(item?.beginDate) || isEmpty(item?.finishDate)) &&
        typeof item === 'object'
      ) {
        result = { message: 'Поле пустое' };
        break;
      }
      if (beginDateUnix > finishDateUnix) {
        result = { message: 'Дата начала должна быть меньше даты конца' };
        break;
      }
    }

    return result;
  }

  // eslint-disable-next-line class-methods-use-this,@typescript-eslint/no-empty-function,react/sort-comp
  onFocus() {}

  render() {
    const {
      schema: { disabled, description, notification, extra },
      error,
      visited,
    } = this.props;
    const { value } = this.state;

    const errorMessage = visited && error && error.message;

    let status = 'default';

    if (errorMessage) {
      status = 'error';
    } else if (
      !isEmpty(value[0]?.beginDate) &&
      !isEmpty(value[0]?.finishDate)
    ) {
      status = 'success';
    }

    return (
      <Field {...this.props.schema} status={status}>
        {extra && extra.hint && (
          <div
            className="alert alert-info hint"
            dangerouslySetInnerHTML={{ __html: extra.hint }}
          />
        )}

        {value.map((item, index) => {
          // set duration only for multiple field
          let minDateStringForMultiple;
          if (this.state.multiple && value?.[index - 1]) {
            if (value[index - 1]?.finishDate) {
              const duration =
                extra?.multiple?.duration === 0 ? 1 : extra?.multiple?.duration;
              const minDate = addDays(
                parse(value[index - 1]?.finishDate, 'dd.MM.yyyy', new Date()),
                duration,
              );
              minDateStringForMultiple = format(minDate, 'dd.MM.yyyy');
            }
          }

          return (
            <div className="input-group-cr">
              <DateTime
                // disabled={typeof extra?.multiple?.duration === 'number' && index > 0 ? true : disabled}
                disabled={disabled}
                label={this.tr(this.props.schema.beginDate)}
                value={
                  typeof extra?.multiple?.duration === 'number' && index > 0
                    ? minDateStringForMultiple
                    : item.beginDate
                }
                minDate={minDateStringForMultiple}
                onFocus={this.onFocus}
                onBlur={() => this.onBlur('begin')}
                onChangeValue={(_value: string) =>
                  this.onChange(_value, 'begin', index)
                }
              />
              <DateTime
                disabled={disabled}
                label={this.tr(this.props.schema.finishDate)}
                value={item.finishDate}
                minDate={minDateStringForMultiple}
                onFocus={this.onFocus}
                onBlur={() => this.onBlur('finish')}
                onChangeValue={(_value: string) =>
                  this.onChange(_value, 'finish', index)
                }
              />

              {notification && (
                <span
                  onClick={this.onOpenNotification}
                  className="btn btn-default"
                >
                  <span className="glyphicon glyphicon-info-sign" />
                </span>
              )}
              {description && (
                <span onClick={this.onClick} className="btn btn-default">
                  <span className="glyphicon glyphicon-question-sign" />
                </span>
              )}
            </div>
          );
        })}

        {notification && this.state.isOpenNotification && (
          <div className="alert alert-warning" role="alert">
            {this.tr(notification)}
          </div>
        )}
        {description && this.state.isOpen && (
          <div className="alert alert-warning" role="alert">
            {this.tr(description)}
          </div>
        )}
        <ErrorAlert errorMessage={errorMessage} />

        {this.state.multiple && !disabled ? (
          <>
            {extra.multiple.maxLength > this.state.value.length ? (
              <>
                <button
                  className="btn btn-primary mr-1"
                  type="button"
                  onClick={this.handlerMultipleAction('add')}
                >
                  Добавить
                </button>
                &nbsp;
              </>
            ) : null}
            <button
              className="btn btn-danger"
              type="button"
              onClick={this.handlerMultipleAction('remove')}
            >
              Удалить
            </button>
          </>
        ) : null}
      </Field>
    );
  }
}

export default RangeCalendar;
